import { FileImageOutlined, WarningOutlined } from '@ant-design/icons';
import { Image, Tooltip } from 'antd';
import React, { useEffect, useState } from 'react';

interface ThumbnailProps {
  /** Thumbnail source URL. Empty/undefined means the field has no value. */
  src?: string | null;
  width: number;
  height: number;
  alt?: string;
  /** Text shown when there is no URL at all (empty field). */
  emptyText?: string;
  /** Text shown when a URL exists but fails to load (404 / 403 / network). */
  errorText?: string;
  /** Enable Ant Design preview-on-click for successfully loaded images. */
  preview?: boolean;
}

type Status = 'loading' | 'loaded' | 'error';

const baseBoxStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '2px',
  borderRadius: '4px',
  textAlign: 'center',
  lineHeight: 1.2,
  overflow: 'hidden',
  padding: '2px',
  boxSizing: 'border-box',
};

/**
 * Thumbnail with three visually distinct states:
 *  - empty:  the field has no value          → neutral gray placeholder
 *  - error:  value exists but load fails     → red warning state (404/403/etc.)
 *  - loaded: value exists and loads          → the image
 */
const Thumbnail: React.FC<ThumbnailProps> = ({
  src,
  width,
  height,
  alt = '',
  emptyText = 'No image',
  errorText = 'Load failed',
  preview = false,
}) => {
  const hasSrc = !!src && src.trim() !== '';
  const [status, setStatus] = useState<Status>('loading');

  // Reset load state whenever the source changes (e.g. table re-render / pagination).
  useEffect(() => {
    setStatus('loading');
  }, [src]);

  // Only show inline text when the box is wide enough to fit it.
  const showText = width >= 60;

  if (!hasSrc) {
    return (
      <Tooltip title={emptyText}>
        <div
          style={{
            ...baseBoxStyle,
            width,
            height,
            color: '#999',
            backgroundColor: '#fafafa',
            border: '1px dashed #d9d9d9',
            fontSize: '11px',
          }}
        >
          <FileImageOutlined style={{ fontSize: '16px' }} />
          {showText && <span>{emptyText}</span>}
        </div>
      </Tooltip>
    );
  }

  if (status === 'error') {
    return (
      <Tooltip title={`${errorText} (${src})`}>
        <div
          style={{
            ...baseBoxStyle,
            width,
            height,
            color: '#cf1322',
            backgroundColor: '#fff1f0',
            border: '1px solid #ffa39e',
            fontSize: '11px',
          }}
        >
          <WarningOutlined style={{ fontSize: '16px' }} />
          {showText && <span>{errorText}</span>}
        </div>
      </Tooltip>
    );
  }

  return (
    <Image
      src={src as string}
      alt={alt}
      width={width}
      height={height}
      style={{ objectFit: 'cover', borderRadius: '4px' }}
      preview={preview}
      onError={() => setStatus('error')}
      onLoad={() => setStatus('loaded')}
      // Disable AntD's own fallback placeholder so our error state is the single source of truth.
      placeholder={false}
    />
  );
};

export default Thumbnail;
